window.__drProductFormQueue = window.__drProductFormQueue || Promise.resolve();
window.__drProductFormQueueDepth = window.__drProductFormQueueDepth || 0;

function enqueueProductFormTask(task) {
  window.__drProductFormQueueDepth++;
  const queuedTask = window.__drProductFormQueue.catch(() => {}).then(() => task());
  window.__drProductFormQueue = queuedTask.catch(() => {});
  return queuedTask.finally(() => {
    window.__drProductFormQueueDepth = Math.max(0, window.__drProductFormQueueDepth - 1);
  });
}

function parseAddLineFromFormData(formData) {
  const variantId = Number(formData.get('id') || 0);
  if (!variantId) return null;

  const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
  const sellingPlan = formData.get('selling_plan');
  const properties = {};

  for (const [key, value] of formData.entries()) {
    if (!key.startsWith('properties[') || !key.endsWith(']')) continue;
    const propKey = key.slice(11, -1);
    if (!propKey) continue;
    properties[propKey] = value;
  }

  const lineItem = { id: variantId, quantity };
  if (sellingPlan !== null && sellingPlan !== '') lineItem.selling_plan = sellingPlan;
  if (Object.keys(properties).length) lineItem.properties = properties;
  return lineItem;
}

function buildAtomicAddPayload({ paidLine, giftLines, cart }) {
  const payload = {
    items: [paidLine, ...(giftLines || [])],
  };

  if (cart) {
    payload.sections = cart.getSectionsToRender().map((section) => section.id);
    payload.sections_url = window.location.pathname;
  }

  return payload;
}

async function atomicAddToCart(payload) {
  const config = fetchConfig('javascript');
  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  config.body = JSON.stringify(payload);
  const response = await fetch(`${routes.cart_add_url}`, config);
  return response.json();
}

function nextCartPaintFrame() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve());
      return;
    }
    setTimeout(resolve, 0);
  });
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);
        const settlementCoordinator = window.drCartSettlementCoordinator;
        const canUseAtomicForThisSubmit =
          window.__drProductFormQueueDepth === 0 && !settlementCoordinator?.isBusy;
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        const executeMutation = () =>
          enqueueProductFormTask(async () => {
            let response = null;
            let usedAtomicAdd = false;
            const paidLine = parseAddLineFromFormData(formData);
            const publishFullCartUpdate = (cartData, source = 'product-form') => {
              if (!cartData || this.error) return Promise.resolve();
              const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
              return publish(PUB_SUB_EVENTS.cartUpdate, {
                source,
                productVariantId: formData.get('id'),
                cartData,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            };
            const startTrackedFollowUp = (task, meta = {}) => {
              const coordinator = window.drCartSettlementCoordinator;
              const runTask = () => Promise.resolve().then(task);
              const followUpPromise = coordinator?.enqueueMutation
                ? coordinator.enqueueMutation(runTask)
                : runTask();

              followUpPromise.catch((e) => {
                console.warn(meta.errorMessage || '[Cart] Follow-up cart settlement failed:', e);
              });
            };
            const shouldTryAtomic =
              !!this.cart &&
              !!paidLine &&
              canUseAtomicForThisSubmit &&
              this.cart.classList.contains('is-empty') &&
              typeof window.drGiftsReconciler?.planAtomicAddGiftLines === 'function';

            if (shouldTryAtomic) {
              try {
                const plan = await window.drGiftsReconciler.planAtomicAddGiftLines({
                  variantId: paidLine.id,
                  quantity: paidLine.quantity,
                  assumeEmpty: true,
                });
                const giftLines = Array.isArray(plan?.giftLines) ? plan.giftLines : [];
                if (giftLines.length > 0) {
                  response = await atomicAddToCart(
                    buildAtomicAddPayload({
                      paidLine,
                      giftLines,
                      cart: this.cart,
                    })
                  );
                  usedAtomicAdd = true;
                }
              } catch (atomicError) {
                console.warn('[DR Gifts] Atomic add plan failed, falling back to reconcile flow:', atomicError);
              }
            }

            if (!response) {
              const config = fetchConfig('javascript');
              config.headers['X-Requested-With'] = 'XMLHttpRequest';
              delete config.headers['Content-Type'];
              config.body = formData;
              response = await fetch(`${routes.cart_add_url}`, config).then((rawResponse) => rawResponse.json());
            }

            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const reconcileCart = window.drGiftsReconciler?.reconcileCart;
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              quickAddModal.hide(true);
              await nextCartPaintFrame();
            }

            CartPerformance.measure("add:paint-updated-sections", () => {
              this.cart.renderContents(response);
            });

            if (typeof reconcileCart === 'function') {
              startTrackedFollowUp(
                async () => {
                  await reconcileCart({
                    intent: usedAtomicAdd ? 'post-change' : 'post-add',
                    cart: usedAtomicAdd ? undefined : response,
                    applySections: true,
                    publishEvent: true,
                    source: 'product-form',
                  });
                },
                {
                  kind: usedAtomicAdd ? 'atomic-add-gifts' : 'post-add-gifts',
                  source: 'product-form',
                  errorMessage: usedAtomicAdd
                    ? '[DR Gifts] Atomic follow-up reconcile failed:'
                    : '[DR Gifts] Reconcile after add failed:',
                }
              );
            } else {
              startTrackedFollowUp(
                async () => {
                  const fullCart = await fetch('/cart.js', { credentials: 'same-origin' }).then((cartResponse) => {
                    if (!cartResponse.ok) throw new Error(`Failed to fetch cart (${cartResponse.status})`);
                    return cartResponse.json();
                  });
                  fullCart.sections = { ...(response.sections || {}), ...(fullCart.sections || {}) };
                  await publishFullCartUpdate(fullCart);
                },
                {
                  kind: 'post-add-cart-refresh',
                  source: 'product-form',
                  errorMessage: '[Cart] Full cart refresh after add failed:',
                }
              );
            }
          })
            .catch((e) => {
              console.error(e);
            })
            .finally(() => {
              this.submitButton.classList.remove('loading');
              if (typeof this.cart?.syncStateFromRenderedContents === 'function') {
                this.cart.syncStateFromRenderedContents();
              } else if (this.cart && this.cart.classList.contains('is-empty')) {
                this.cart.classList.remove('is-empty');
              }
              if (!this.error) this.submitButton.removeAttribute('aria-disabled');
              this.querySelector('.loading__spinner').classList.add('hidden');

              CartPerformance.measureFromEvent("add:user-action", evt);
            });

        if (settlementCoordinator?.enqueueMutation) {
          settlementCoordinator.enqueueMutation(executeMutation);
          return;
        }

        const runTrackedMutation = window.drGiftsReconciler?.runTrackedMutation;
        if (typeof runTrackedMutation === 'function') {
          runTrackedMutation(executeMutation);
          return;
        }

        executeMutation();
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
